import { compareObjects } from './object'
import { Money } from '@afterpay/types'

export const AddMoneyErrorMessages = {
  InvalidInput: 'Tried to add with no Money objects',
  CurrenciesMismatched: 'Tried to add mismatched currencies',
} as const

interface AddMoneyConfig {
  moneys: Money[]
}

/**
 *
 * @param {object} AddMoneyConfig an object with the Money variables you want to add together
 * @returns {string} the total amount of the Money variables, as a string
 */
export const addMoneys = ({ moneys }: AddMoneyConfig): string => {
  validate(moneys)

  let totalCents = 0
  for (const { amount } of moneys) {
    const [whole, fraction = ''] = amount.split('.')
    const twoDigits = (fraction + '00').slice(0, 2)
    const cents = typeof whole === 'string' ? parseInt(whole, 10) * 100 + parseInt(twoDigits, 10) : 0
    totalCents += cents
  }

  const dollars = Math.trunc(totalCents / 100)
  const centsPart = (totalCents % 100).toString().padStart(2, '0')
  return `${dollars}.${centsPart}`
}

const validate = (moneys: Money[]): void => {
  if (moneys.length === 0) {
    throw Error(AddMoneyErrorMessages.InvalidInput)
  }
  if (!isSameCurrencies(moneys)) {
    throw Error(AddMoneyErrorMessages.CurrenciesMismatched)
  }
}

const isSameCurrencies = (moneys: Money[]): boolean => {
  const moneysAsCurrencies = moneys.map((m) => m.currency)
  const currencies = new Set(moneysAsCurrencies)
  const allSame = currencies.size === 1
  return allSame
}

/**
 *
 * @param {array} moneys array of Money objects
 * @returns {number} the index of the highest Money object in the array
 */
export const getHighestAmountIndex = (moneys: Money[]): number => {
  if (moneys.length === 0) {
    return -1
  }

  let highestIndex = -1
  let highestNumber = 0

  moneys.every((val, idx) => {
    const currentAmt = Number(val.amount)

    if (isNaN(currentAmt) || val.amount.length === 0) {
      return false
    }

    if (currentAmt > highestNumber) {
      highestIndex = idx
      highestNumber = currentAmt
    }

    return true
  })

  return highestIndex
}

/**
 * @param {array} moneys array of Money objects
 * @returns {boolean} if true, all Money objects are same.
 */
export const allAmountsSame = (moneys: Money[]): boolean => {
  const [moneyReference] = moneys
  return moneys.length > 1 && moneys.every((money) => compareObjects(money, moneyReference))
}
